import { Account, Cart, nativeAppHeaders } from '@ynap/api-utils'
import { delay } from '@ynap/promise-utils'
import { EDIT_ADDRESS_REQUEST, EDIT_ADDRESS_SUCCESS, EDIT_ADDRESS_ERROR } from '../constants/ActionTypes'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import wrapFetchWithJSON from './common/wrapFetchWithJSON'
import { getCurrentShippingAddress } from '../selectors/getShippingAddressStep'
import { getLocale } from '@ynap/cart-state'
import { trimValues } from '@ynap/form'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { getCurrentBillingAddress, getCurrentPaymentOption } from '../selectors/getPaymentStep'
import { cleanAddressEmptyValues } from 'app/utils/formatting'

import type { Dispatch } from 'redux'
import type { StoreState } from 'app/state/types'
import type { Address, ShoppingCart } from 'app/types/common-types'

type UpdateAddressResponseData = {
    addressId: number
}

type SetShippingAddressResponseData = {
    shoppingCart: ShoppingCart
}

function editAddressRequest() {
    return {
        type: EDIT_ADDRESS_REQUEST,
    }
}

function editAddressSuccess(data: UpdateAddressResponseData | ShoppingCart) {
    return {
        type: EDIT_ADDRESS_SUCCESS,
        payload: data,
    }
}

function editAddressError(error: unknown) {
    return {
        type: EDIT_ADDRESS_ERROR,
        error,
    }
}

export default function editAddress(address: Address) {
    const cleanedAddress = cleanAddressEmptyValues(address)
    return (dispatch: Dispatch, getState: () => StoreState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const currentShippingAddress = getCurrentShippingAddress(state)
        const currentBillingAddress = getCurrentBillingAddress(state)
        const currentPaymentOption = getCurrentPaymentOption(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        // we don't want to send the email1 anymore, so we delete it from the addresses that already have it
        if ('email1' in cleanedAddress) {
            delete address.email1
        }

        const [paymentMethodId, cardId] = currentPaymentOption?.id?.split('/') || []
        let updateBillingAddress = currentBillingAddress?.addressId === address?.addressId
        cleanedAddress.externalAddressValidation = 'true'
        dispatch(editAddressRequest())
        const promise = fetchJSON(Account.updateAddress({ address: trimValues(cleanedAddress), ...watsonConfig })).then(({ addressId } : UpdateAddressResponseData) => {
            // call setShippingAddress if we are updating the current shipping address
            // as the cart addressId doesn't get updated by WCS otherwise
            if (updateBillingAddress) {
                fetchJSON(
                    Cart?.setPaymentInstruction({
                        paymentInstruction: [
                            {
                                payMethodId: paymentMethodId,
                                billing_address_id: Number(addressId),
                            },
                        ],
                        clearExisting: 'false',
                        ...watsonConfig,
                        langId,
                        additionalHeaders,
                    })
                )
            }
            if (currentShippingAddress && address.addressId === currentShippingAddress.addressId) {
                return fetchJSON(Cart.setShippingAddress({ addressId, ...watsonConfig, langId, additionalHeaders })).then(({ shoppingCart }: SetShippingAddressResponseData) => ({
                    shoppingCart,
                    addressId,
                }))
            }
            // otherwise just get cart - current shipping address was not updated.
            return fetchJSON(Cart.getCart({ ...watsonConfig, langId, additionalHeaders })).then((shoppingCart: ShoppingCart) => ({
                shoppingCart,
                addressId,
            }))
        })

        return delay(promise, getDelay(state))
            .then((data: UpdateAddressResponseData | ShoppingCart) => dispatch(editAddressSuccess(data)))
            .catch((error: unknown) => {
                dispatch(editAddressError(error))
                throw error
            })
    }
}
